import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Remembered for the length of the visit, not forever.
 *
 * The preloader is a first-impression device: it should play when someone
 * arrives and then get out of the way. It was replaying on every reload and
 * every return to the homepage from another page — including a nav click on
 * Work, which routes to /#work — so the same eight words kept interrupting
 * someone already reading the site.
 *
 * sessionStorage rather than localStorage: a later, separate visit should still
 * get the opening. This only suppresses repeats within one sitting.
 */
const SEEN_KEY = "depmont:preloader-seen";

// sessionStorage is not merely empty when blocked — reading it throws in some
// privacy modes. Failing to remember is survivable; it just plays again.
const hasPlayed = () => {
    try {
        return sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {
        return false;
    }
};

const markPlayed = () => {
    try {
        sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
        /* ignore */
    }
};

export default function Preloader() {
    // Read during the first render rather than in an effect. Effects run after
    // paint, so checking there would show a returning visitor a frame of black
    // before it cleared.
    const [done, setDone] = useState(hasPlayed);

    useEffect(() => {
        if (done) return;
        // Marked as it starts, not when it finishes, so navigating away
        // mid-animation still counts as having seen it.
        markPlayed();
        const t = setTimeout(() => setDone(true), 1600);
        return () => clearTimeout(t);
    }, [done]);

    return (
        <AnimatePresence>
            {!done && (
                <motion.div
                    key="preloader"
                    initial={{ y: 0 }}
                    exit={{
                        y: "-100%",
                        transition: {
                            duration: 1.1,
                            ease: [0.83, 0, 0.17, 1],
                        },
                    }}
                    className="fixed inset-0 z-[100] bg-[#1B1B1B] text-[#F6F3EC] flex flex-col justify-between p-6 md:p-10 lg:p-14"
                >
                    <div className="flex justify-between items-start eyebrow text-[#F6F3EC]/60">
                        <span>Depmont</span>
                        <span>Est. 2026</span>
                    </div>

                    <div className="flex items-end justify-between w-full">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.1 }}
                            className="font-serif-editorial italic text-4xl md:text-6xl lg:text-7xl leading-none max-w-2xl"
                        >
                            Building brands that outlast trends.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="hidden md:block eyebrow text-[#F6F3EC]/60 text-right"
                        >
                            <div>Loading —</div>
                            <div className="mt-1">Volume 01</div>
                        </motion.div>
                    </div>

                    <div className="w-full h-px bg-[#F6F3EC]/15 relative overflow-hidden">
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{
                                duration: 1.4,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="absolute inset-y-0 left-0 w-full bg-[#A8844F] origin-left"
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
